// The write-only value inputs. Uncontrolled on purpose: the value lives in the DOM input
// until submit, is read once from the form, sealed and cleared; it never enters React
// state, a query cache or a log. (RizzUI's Password keeps only its show/hide flag in state.)
import { useRef } from "react";
import { Button, Input, Password, Textarea } from "rizzui";

import { useT } from "../i18n";
import { Hinted } from "./SecretDrawer";
import type { ValueShape } from "./templates";

const common = { autoComplete: "off", spellCheck: false, required: true } as const;

/** A profile pasted or loaded from a file straight into the textarea (never React state). */
function FileValue({ shape }: { shape: Extract<ValueShape, { kind: "file" }> }) {
  const t = useT().secrets.templates;
  const area = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const load = async (file: File | undefined) => {
    if (file && area.current) area.current.value = await file.text();
    if (picker.current) picker.current.value = "";
  };
  return (
    <div className="space-y-2">
      <Textarea ref={area} label={shape.label} name="value" rows={8} className="font-mono text-[12.5px]" {...common} />
      <input ref={picker} type="file" accept={shape.accept} hidden aria-label={t.loadFile} onChange={(e) => void load(e.target.files?.[0])} />
      <Button type="button" size="sm" variant="outline" onClick={() => picker.current?.click()}>
        {t.loadFile}
      </Button>
      <p className="text-[13px] text-gray-500">{shape.help}</p>
    </div>
  );
}

export function ValueInputs({ shape }: { shape: ValueShape }) {
  const t = useT().secrets.forms.value;
  if (shape.kind === "file") return <FileValue shape={shape} />;
  if (shape.kind === "single") {
    return (
      <Hinted hint={t.help}>
        <Password label={shape.label} name="value" placeholder={t.placeholder} {...common} />
      </Hinted>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {shape.fields.map((f) =>
          f.masked ? (
            <Password key={f.name} label={f.label} name={f.name} {...common} />
          ) : (
            <Input key={f.name} label={f.label} name={f.name} type={f.input?.type ?? "text"} placeholder={f.input?.placeholder} {...common} />
          ),
        )}
      </div>
      <p className="text-[13px] text-gray-500">{shape.help ?? t.help}</p>
    </div>
  );
}
